import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { 
  Package, Plus, Upload, RefreshCw, 
  Truck, AlertTriangle, CheckCircle, Archive,
  Database, Search, Filter as FilterIcon,
  Loader2, Info
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Components
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import InventoryStats from "../../components/operationsAssociate/InventoryStats";
import InventoryTable from "../../components/operationsAssociate/InventoryTable";
import InventoryTabs from "../../components/operationsAssociate/InventoryTabs";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";

// Modals
import AddItemModal from "../../components/operationsAssociate/AddItemModal";
import BulkItemUploadModal from "../../components/operationsAssociate/BulkItemUploadModal";

const API_URL = "https://ims.holdthechild.net";

const InventoryManagementPage = () => {
    // --- UI State ---
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // --- Data State ---
    const [items, setItems] = useState([]);
    const [modal, setModal] = useState({ add: false, bulk: false });

    const fetchItems = useCallback(async (isRefresh = false) => {
        isRefresh ? setRefreshing(true) : setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/api/inventory`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data.data || []);
        } catch (err) {
            setError("Inventory database synchronization offline.");
            toast.error("Failed to sync inventory registry ❌");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const toggleModal = (type, state) => {
        setModal(prev => ({ ...prev, [type]: state }));
        if (!state) fetchItems(true);
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-10">
                        
                        {/* Hub Navigation */}
                        <div className="flex flex-col items-center">
                            <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-gray-100 inline-block mb-8">
                                <InventoryTabs />
                            </div>
                            
                            <PremiumHeader 
                                title="Inventory" 
                                lastWordAccent="Control" 
                                subtitle="Real-time monitoring and logistics management for consumable supplies and operational resource stock levels." 
                            />
                        </div>

                        {/* Action Bar */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex gap-4 w-full md:w-auto order-2 md:order-1">
                                <button
                                    onClick={() => toggleModal('bulk', true)}
                                    className="flex-1 md:flex-none border-2 border-gray-900 bg-white text-gray-900 px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
                                >
                                    <Upload size={16}/> Bulk Load Registry
                                </button>
                                <button
                                    onClick={() => toggleModal('add', true)}
                                    className="flex-1 md:flex-none bg-orange-600 text-white px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-orange-700 transition-all transform hover:-translate-y-1 shadow-xl shadow-orange-100 flex items-center justify-center gap-2"
                                >
                                    <Plus size={18}/> Add New SKU
                                </button>
                            </div>
                            
                            <div className="flex items-center gap-3 bg-blue-50/50 p-2 rounded-2xl order-1 md:order-2">
                                <div className="p-2 bg-white rounded-xl shadow-sm">
                                    <Database className="text-blue-600" size={14} />
                                </div>
                                <span className="text-[10px] font-black text-blue-700 uppercase tracking-widest mr-2">Warehousing Sync: Live</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                                <Archive className="text-orange-200 animate-bounce mb-6" size={56} />
                                <p className="text-[11px] font-black uppercase tracking-[0.4em] text-gray-400">Accessing Warehouse Stock Matrix...</p>
                            </div>
                        ) : error ? (
                            <div className="p-16 text-center bg-red-50 rounded-[3rem] border border-red-100">
                                <div className="p-6 bg-red-100/50 rounded-full w-fit mx-auto mb-6">
                                    <Info className="text-red-600" size={32} />
                                </div>
                                <h3 className="text-xl font-black text-red-900 uppercase tracking-tight mb-2">Synchronization Fail</h3>
                                <p className="text-red-600/70 font-medium mb-8 max-w-md mx-auto">{error}</p>
                                <button onClick={() => fetchItems()} className="bg-red-600 text-white px-10 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest flex items-center gap-3 mx-auto hover:bg-red-700 shadow-xl shadow-red-100">
                                    <RefreshCw size={16} /> Reconnect Ledger
                                </button>
                            </div>
                        ) : (
                            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <PremiumStatCard 
                                        title="SKU Volume" 
                                        value={items.length} 
                                        icon={<Package className="text-blue-600" />} 
                                        iconBg="bg-blue-50"
                                        description="Unique product identifiers"
                                    />
                                    <PremiumStatCard 
                                        title="Critical Alerts" 
                                        value={items.filter(i => i.quantity <= i.minThreshold).length} 
                                        icon={<AlertTriangle className="text-orange-600" />} 
                                        iconBg="bg-orange-50"
                                        description="Items below threshold"
                                    />
                                    <PremiumStatCard 
                                        title="Steady Supply" 
                                        value={items.filter(i => i.quantity > i.minThreshold).length} 
                                        icon={<CheckCircle className="text-emerald-600" />} 
                                        iconBg="bg-emerald-50"
                                        description="Optimal stock status"
                                    />
                                    <PremiumStatCard 
                                        title="Inbound Flow" 
                                        value="Active" 
                                        icon={<Truck className="text-purple-600" />} 
                                        iconBg="bg-purple-50"
                                        description="Logistic stream status"
                                    />
                                </div>
                                
                                {refreshing && (
                                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-3 px-6 py-3 bg-blue-50 text-blue-600 rounded-full w-fit mx-auto shadow-sm border border-blue-100">
                                        <RefreshCw className="animate-spin" size={14} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Optimizing Supply Ledger...</span>
                                    </motion.div>
                                )}

                                <div className="space-y-8">
                                    <InventoryStats items={items} />
                                    
                                    <PremiumCard icon={<Archive size={18} />} title="Global Stock Registry">
                                        <div className="p-2 overflow-hidden">
                                            <InventoryTable items={items} onUpdate={() => fetchItems(true)} />
                                        </div>
                                    </PremiumCard>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </main>
            </div>

            {/* --- Modals Overlay --- */}
            <AnimatePresence>
                {(modal.add || modal.bulk) && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }}
                            onClick={() => toggleModal(modal.add ? 'add' : 'bulk', false)}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-100"
                        >
                            {modal.add && <AddItemModal closeModal={() => toggleModal('add', false)} onAddItem={() => fetchItems(true)} />}
                            {modal.bulk && (
                                <BulkItemUploadModal
                                    closeModal={() => toggleModal('bulk', false)}
                                    onAssetsUploaded={() => fetchItems(true)}
                                />
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default InventoryManagementPage;